import { supabase } from "./supabase";

export interface PasswordUpdateResult {
  success: boolean;
  error?: string;
  retryable?: boolean;
}

/**
 * Utility function to update user password with retry logic and better error handling
 */
export const updatePasswordWithRetry = async (
  newPassword: string,
  maxRetries: number = 2,
  timeoutMs: number = 30000
): Promise<PasswordUpdateResult> => {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("Password update timed out")),
          timeoutMs
        )
      );

      // Update password
      const updatePromise = supabase.auth.updateUser({
        password: newPassword,
      });

      // Race between update and timeout
      const { error } = await Promise.race([updatePromise, timeoutPromise]);

      if (!error) {
        return { success: true };
      }

      // Handle specific errors
      const errorResult = handlePasswordUpdateError(error);

      // If error is not retryable, return immediately
      if (!errorResult.retryable) {
        return errorResult;
      }

      lastError = error;

      // Wait before retry (exponential backoff)
      if (attempt <= maxRetries) {
        const waitTime = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    } catch (error) {
      console.error(
        `💥 Exception in password update attempt ${attempt}:`,
        error
      );
      lastError = error instanceof Error ? error : new Error("Unknown error");

      // Handle timeout specifically
      if (lastError.message.includes("timed out")) {
        if (attempt <= maxRetries) {
          continue;
        }
        return {
          success: false,
          error:
            "Operasi timeout setelah beberapa percobaan. Silakan coba lagi.",
          retryable: true,
        };
      }

      // Handle network errors
      if (
        lastError.message.includes("fetch") ||
        lastError.message.includes("network")
      ) {
        if (attempt <= maxRetries) {
          continue;
        }
        return {
          success: false,
          error: "Masalah koneksi. Periksa internet Anda dan coba lagi.",
          retryable: true,
        };
      }

      // For other errors, don't retry
      break;
    }
  }

  // If we get here, all retries failed
  return {
    success: false,
    error:
      lastError?.message ||
      "Gagal mengubah password setelah beberapa percobaan",
    retryable: true,
  };
};

/**
 * Handle specific password update errors and determine if they're retryable
 */
const handlePasswordUpdateError = (error: unknown): PasswordUpdateResult => {
  const message = error?.message || "";

  // Session/authentication errors (not retryable)
  if (
    message.includes("Invalid login credentials") ||
    message.includes("Invalid user") ||
    message.includes("User not found") ||
    message.includes("JWT expired")
  ) {
    return {
      success: false,
      error: "Sesi tidak valid. Silakan login kembali.",
      retryable: false,
    };
  }

  // Password validation errors (not retryable)
  if (
    message.includes("Password should be at least") ||
    message.includes("Password must contain") ||
    message.includes("weak password")
  ) {
    return {
      success: false,
      error: "Password baru tidak memenuhi persyaratan keamanan",
      retryable: false,
    };
  }

  // Rate limiting errors (retryable with delay)
  if (
    message.includes("rate limit") ||
    message.includes("too many") ||
    message.includes("429")
  ) {
    return {
      success: false,
      error:
        "Terlalu banyak percobaan. Silakan coba lagi dalam beberapa menit.",
      retryable: false, // Don't retry immediately for rate limits
    };
  }

  // Server errors (retryable)
  if (
    message.includes("500") ||
    message.includes("502") ||
    message.includes("503") ||
    message.includes("504") ||
    message.includes("Internal server error")
  ) {
    return {
      success: false,
      error: "Server sedang bermasalah. Silakan coba lagi.",
      retryable: true,
    };
  }

  // Network/connection errors (retryable)
  if (
    message.includes("fetch") ||
    message.includes("network") ||
    message.includes("connection") ||
    message.includes("timeout")
  ) {
    return {
      success: false,
      error: "Masalah koneksi. Periksa internet Anda dan coba lagi.",
      retryable: true,
    };
  }

  // Unknown errors (retryable)
  return {
    success: false,
    error: message || "Terjadi kesalahan tidak terduga",
    retryable: true,
  };
};

/**
 * Validate password strength
 */
export const validatePasswordStrength = (password: string): string[] => {
  const errors = [];

  if (password.length < 8) {
    errors.push("minimal 8 karakter");
  }

  if (!/[A-Z]/.test(password)) {
    errors.push("huruf besar");
  }

  if (!/[a-z]/.test(password)) {
    errors.push("huruf kecil");
  }

  if (!/[0-9]/.test(password)) {
    errors.push("angka");
  }

  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    errors.push("karakter khusus (!@#$%^&* dll)");
  }

  return errors;
};

/**
 * Check if two passwords match
 */
export const validatePasswordMatch = (
  password: string,
  confirmPassword: string
): boolean => {
  return password === confirmPassword;
};

/**
 * Check if new password is different from current password
 */
export const validatePasswordDifferent = (
  currentPassword: string,
  newPassword: string
): boolean => {
  return currentPassword !== newPassword;
};
